/* eslint-disable no-nested-ternary */
import { arrayOf, bool, oneOf, oneOfType, shape, string } from "prop-types";
import pureProps from "../utils/pureProps";
import { colorPropTypes } from "./sx";

export const utilityProps = {
	opacity: null,
	border: null,
	display: null,
	justify: null,
	align: {
		self: null,
		items: null,
		content: null,
	},
	offset: null,
	order: null,

	radius: null,
	bgColor: null,
	bgImage: null,
	shadow: null,
	overflow: null,
	position: null,
	sm: {
		opacity: null,
		border: null,
		radius: null,
		display: null,
		justify: null,
		align: {
			self: null,
			items: null,
			content: null,
		},
		offset: null,
		order: null,
	},
	md: {
		opacity: null,
		border: null,
		radius: null,
		display: null,
		justify: null,
		align: {
			self: null,
			items: null,
			content: null,
		},
		offset: null,
		order: null,
	},
	lg: {
		opacity: null,
		border: null,
		radius: null,
		display: null,
		justify: null,
		align: {
			self: null,
			items: null,
			content: null,
		},
		offset: null,
		order: null,
	},
};

const borderRadiusTypes = oneOf([
	0,
	1,
	2,
	3,
	4,
	5,
	6,
	7,
	8,
	9,
	10,
	11,
	12,
	13,
	14,
	15,
	16,
	17,
	18,
	19,
	20,
	"50p",
	"1000px",
]);

const borderPropTypes = shape({
	width: oneOf([0, 1, 2, 3, 4]),
	color: colorPropTypes,
});

const positionPropTypes = shape({
	position: oneOf(["relative", "absolute", "fixed", "sticky"]),
	top: oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20]),
	bottom: oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20]),
	left: oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20]),
	right: oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20]),
	zIndex: oneOf(["m1", 1, 0]),
});

const adaptivePropTypes = {
	border: oneOfType([
		shape({
			top: borderPropTypes,
			right: borderPropTypes,
			bottom: borderPropTypes,
			left: borderPropTypes,
		}),
		borderPropTypes,
	]),
	radius: oneOfType([
		shape({
			topLeft: borderRadiusTypes,
			topRight: borderRadiusTypes,
			bottomRight: borderRadiusTypes,
			bottomLeft: borderRadiusTypes,
		}),
		borderRadiusTypes,
	]),
	display: oneOf(["none", "block", "inline-block", "flex", "flex-vertical"]),
	justify: oneOf(["start", "end", "center", "between", "around", "evenly"]),
	align: shape({
		items: oneOf(["start", "end", "center", "baseline", "stretch"]),
		self: oneOf(["start", "end", "center", "baseline", "stretch"]),
		content: oneOf(["start", "end", "center", "between", "around", "stretch"]),
	}),
	offset: oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
	order: oneOf([1, 2, 3, 4, 5, 6]),
	opacity: oneOf([0, 50, 100]),
};

export const utilityPropTypes = shape({
	bgColor: colorPropTypes,
	bgImage: arrayOf(
		shape({
			url: string.isRequired,
			position: string.isRequired,
			repeat: oneOf(["no-repeat", "repeat"]).isRequired,
			size: string.isRequired,
		}),
	),
	shadow: oneOf([string, bool]),
	overflow: oneOf(["hidden", "scrollX", "scrollY"]),
	position: positionPropTypes,
	...adaptivePropTypes,
	sm: shape(adaptivePropTypes),
	md: shape(adaptivePropTypes),
	lg: shape(adaptivePropTypes),
});

export const utilityClassName = ({ ...props }) => {
	const {
		radius,
		border,
		bgColor,
		shadow,
		overflow,

		display,
		justify,
		align,
		offset,
		order,
		opacity,
		position,
		sm,
		md,
		lg,
	} = props;
	const borderRadiusClassNames = (borderRadiusProps, size = null) => {
		const isNumber = value => typeof value === "number" && value >= 0;
		const is50 = value => typeof value === "string" && value === "50p";
		const is1000 = value => typeof value === "string" && value === "1000px";
		const mediaSize = size ? `--${size}` : "";
		if (
			(borderRadiusProps && !is50(borderRadiusProps) && !is1000(borderRadiusProps)) ||
			isNumber(borderRadiusProps) ||
			is50(borderRadiusProps) ||
			is1000(borderRadiusProps)
		) {
			if (
				is50(borderRadiusProps) ||
				is1000(borderRadiusProps) ||
				Object.keys(borderRadiusProps).length === 0
			) {
				// eslint-disable-next-line no-nested-ternary
				return isNumber(borderRadiusProps)
					? [`border-radius-${borderRadiusProps}px${mediaSize}`]
					: is50(borderRadiusProps)
					? [`border-radius-${borderRadiusProps}${mediaSize}`]
					: is1000(borderRadiusProps)
					? [`border-radius-${borderRadiusProps}${mediaSize}`]
					: [];
			}
			const pureBorderRadiusProps = pureProps(borderRadiusProps);
			return Object.keys(pureBorderRadiusProps).map(key => {
				const value = pureBorderRadiusProps[key];
				return isNumber(value)
					? `border-radius-${key
							.split(/(?=[A-Z])/)
							.join("-")
							.toLowerCase()}-${value}px${mediaSize}`
					: "";
			});
		}
		return [];
	};
	const borderClassNames = (borderProps = null, size = null) => {
		const borderClassNameTemplate = (width, color, mediaSize, key = "") => {
			const keyValue = key ? `-${key}` : "";
			const widthValue = width === 0 ? "none" : `${width}px`;
			const colorValue = color ? `-${color}` : "";
			const mediaSizeValue = mediaSize ? `--${mediaSize}` : "";
			return `border${keyValue}-${widthValue}${colorValue}${mediaSizeValue}`;
		};
		if (borderProps) {
			const sideProps =
				borderProps.top || borderProps.right || borderProps.bottom || borderProps.left || {};
			if (Object.keys(sideProps).length === 0) {
				const { width, color = null } = borderProps;
				return typeof width !== "undefined" && width >= 0
					? [borderClassNameTemplate(width, color, size)]
					: [];
			}
			const pureBorderProps = pureProps(borderProps);
			return Object.keys(pureBorderProps).map(key => {
				const { width, color = null } = pureBorderProps[key];
				return typeof width !== "undefined" && width >= 0
					? borderClassNameTemplate(width, color, size, key)
					: "";
			});
		}
		return [];
	};
	const positionClassNames = positionProps => {
		const isNumber = value => typeof value === "number" && value >= 0;
		const classes = [];
		const hasProps = positionProps && Object.keys(positionProps).length > 0;
		if (hasProps && positionProps.position !== null) {
			classes.push(`position-${positionProps.position}`);
		}
		if (hasProps && isNumber(positionProps.top)) {
			classes.push(`top-${positionProps.top}`);
		}
		if (hasProps && isNumber(positionProps.right)) {
			classes.push(`right-${positionProps.right}`);
		}
		if (hasProps && isNumber(positionProps.bottom)) {
			classes.push(`bottom-${positionProps.bottom}`);
		}
		if (hasProps && isNumber(positionProps.left)) {
			classes.push(`left-${positionProps.left}`);
		}
		if (hasProps && (isNumber(positionProps.zIndex) || typeof positionProps.zIndex === "string")) {
			classes.push(`z-index-${positionProps.zIndex}`);
		}
		return classes;
	};

	const PREFIX_CLASS_BG_COLOR = "bg-color-";
	const PREFIX_CLASS_BOX_SHADOW = "box-shadow";
	const PREFIX_CLASS_DISPLAY = "display-";
	const PREFIX_CLASS_OVERFLOW = "overflow--";
	const PREFIX_CLASS_JUSTIFY = "justify-content-";
	const PREFIX_CLASS_ALIGN = "align-";
	const PREFIX_CLASS_OFFSET = "offset-";
	const PREFIX_CLASS_ORDER = "order-";

	return [
		...borderClassNames(border),
		...borderRadiusClassNames(radius),
		bgColor && `${PREFIX_CLASS_BG_COLOR}${bgColor}`,
		shadow && PREFIX_CLASS_BOX_SHADOW,
		overflow && `${PREFIX_CLASS_OVERFLOW}${overflow}`,

		display && `${PREFIX_CLASS_DISPLAY}${display}`,
		justify && `${PREFIX_CLASS_JUSTIFY}${justify}`,
		align && align.self && `${PREFIX_CLASS_ALIGN}self-${align.self}`,
		align && align.items && `${PREFIX_CLASS_ALIGN}items-${align.items}`,
		align && align.content && `${PREFIX_CLASS_ALIGN}content-${align.content}`,
		offset && `${PREFIX_CLASS_OFFSET}${offset}`,
		order && `${PREFIX_CLASS_ORDER}${order}`,
		opacity && `opacity-${opacity}`,
		...positionClassNames(position),
		...borderClassNames(sm && sm.border, "sm"),
		...borderRadiusClassNames(sm && sm.radius, "sm"),
		sm && sm.display && `${PREFIX_CLASS_DISPLAY}${sm.display}--sm`,
		sm && sm.overflow && `${PREFIX_CLASS_OVERFLOW}${sm.overflow}--sm`,
		sm && sm.justify && `${PREFIX_CLASS_JUSTIFY}${sm.justify}--sm`,
		sm && sm.align && sm.align.self && `${PREFIX_CLASS_ALIGN}self-${sm.align.self}--sm`,
		sm && sm.align && sm.align.items && `${PREFIX_CLASS_ALIGN}items-${sm.align.items}--sm`,
		sm && sm.align && sm.align.content && `${PREFIX_CLASS_ALIGN}content-${sm.align.content}--sm`,
		sm && sm.offset && `${PREFIX_CLASS_OFFSET}${sm.offset}--sm`,
		sm && sm.order && `${PREFIX_CLASS_ORDER}${sm.order}--sm`,
		sm && sm.opacity && `opacity-${sm.opacity}--sm`,

		...borderClassNames(md && md.border, "md"),
		...borderRadiusClassNames(md && md.radius, "md"),
		md && md.display && `${PREFIX_CLASS_DISPLAY}${md.display}--md`,
		md && md.overflow && `${PREFIX_CLASS_OVERFLOW}${md.overflow}--md`,
		md && md.justify && `${PREFIX_CLASS_JUSTIFY}${md.justify}--md`,
		md && md.align && md.align.self && `${PREFIX_CLASS_ALIGN}self-${md.align.self}--md`,
		md && md.align && md.align.items && `${PREFIX_CLASS_ALIGN}items-${md.align.items}--md`,
		md && md.align && md.align.content && `${PREFIX_CLASS_ALIGN}content-${md.align.content}--md`,
		md && md.offset && `${PREFIX_CLASS_OFFSET}${md.offset}--md`,
		md && md.order && `${PREFIX_CLASS_ORDER}${md.order}--md`,
		md && md.opacity && `opacity-${md.opacity}--md`,

		...borderClassNames(lg && lg.border, "lg"),
		...borderRadiusClassNames(lg && lg.radius, "lg"),
		lg && lg.display && `${PREFIX_CLASS_DISPLAY}${lg.display}--lg`,
		lg && lg.overflow && `${PREFIX_CLASS_OVERFLOW}${lg.overflow}--lg`,
		lg && lg.justify && `${PREFIX_CLASS_JUSTIFY}${lg.justify}--lg`,
		lg && lg.align && lg.align.self && `${PREFIX_CLASS_ALIGN}self-${lg.align.self}--lg`,
		lg && lg.align && lg.align.items && `${PREFIX_CLASS_ALIGN}items-${lg.align.items}--lg`,
		lg && lg.align && lg.align.content && `${PREFIX_CLASS_ALIGN}content-${lg.align.content}--lg`,
		lg && lg.offset && `${PREFIX_CLASS_OFFSET}${lg.offset}--lg`,
		lg && lg.order && `${PREFIX_CLASS_ORDER}${lg.order}--lg`,
		lg && lg.opacity && `opacity-${lg.opacity}--lg`,
	];
};

export const utilityBackgroundImages = ({ ...props }) => {
	const { bgImage } = props;
	if (bgImage && bgImage.length) {
		const urls = bgImage.map(image => `url("${image.url}")`).join(",");
		const positions = bgImage.map(image => image.position).join(",");
		const repeats = bgImage.map(image => image.repeat).join(",");
		const sizes = bgImage.map(image => image.size).join(",");
		return {
			style: {
				backgroundImage: urls,
				backgroundPosition: positions,
				backgroundRepeat: repeats,
				backgroundSize: sizes,
			},
		};
	}
	return {};
};
